import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, ClipboardCopy, Edit, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MaskBadge } from "@/components/ui/mask-badge";
import { useMeasurementsByMeasurementRequest } from "@/hooks/useMeasurements";
import { useDeleteMeasurementRequest, useMeasurementRequest } from "@/hooks/useMeasurementRequests";
import { useFeatureConfig } from "@/hooks/useFeatureConfig";
import { PholisScanCard } from "./components/PholisScanCard";
import DeleteActionDialog from "../../shared/DeleteActionDialog";
import { UpdateMeasurementRequestDialogWrapper } from "./components/UpdateMeasurementRequestDialogWrapper";
import { MeasurementRequestMessages } from "./components/MeasurementRequestMessages";
import { useTestLabels } from "@/hooks/useTestLabels";
import { useChipPos } from "@/hooks/useChipPos";
import { LoadingDisplay } from "../../shared/LoadingDisplay";
import { GenericErrorDisplay } from "../../shared/ErrorDisplay";
import { NotFoundDisplay } from "../../shared/NotFoundDisplay";
import Tooltip from "../../shared/Tooltip";
import { WarningAlert } from "../../shared/Alerts";
import { WaferSection } from "./components/WaferSection";
import { displayDate, isMeasurementRequestDeletable } from "./MeasurementRequestDetails.functions";
import { MeasurementDiagnostics } from "./components/MeasurementDiagnostics";
import { usePermissionContext } from "@/permissions";

export function MeasurementRequestDetails() {
  const { measurementRequestId } = useParams();
  const { mutate } = useSWRConfig();
  const navigate = useNavigate();
  const { hasPermission } = usePermissionContext();

  const { trigger: deleteRequest, isMutating: isExecutingDeletion } = useDeleteMeasurementRequest();

  const pholis = useFeatureConfig("pholis");
  const {
    error: isMeasurementRequestError,
    data: measurementRequest,
    isLoading: isMeasurementRequestLoading,
    mutate: refetchMeasurementRequestComments,
  } = useMeasurementRequest(Number(measurementRequestId));
  const {
    error: isMeasurementsError,
    data: measurements,
    isLoading: isMeasurementsLoading,
  } = useMeasurementsByMeasurementRequest(Number(measurementRequestId));

  const {
    error: isTestLabelsError,
    data: testLabels,
    isLoading: isTestLabelsLoading,
  } = useTestLabels();
  const { error: isChipPosError, data: chipPos, isLoading: isChipPosLoading } = useChipPos();

  const [isDeleteActionDialogOpen, setIsDeleteActionDialogOpen] = useState(false);
  const [showUpdateMeasurementRequestDialog, setShowUpdateMeasurementRequestDialog] =
    useState(false);

  if (
    isMeasurementRequestLoading ||
    isTestLabelsLoading ||
    isChipPosLoading ||
    isMeasurementsLoading
  ) {
    return <LoadingDisplay />;
  }

  if (isMeasurementRequestError || isTestLabelsError || isChipPosError || isMeasurementsError) {
    return <GenericErrorDisplay />;
  }

  if (!measurementRequest) {
    return (
      <NotFoundDisplay redirectUrl="/measurement-requests" text="Measurement Request not found" />
    );
  }

  const onDelete = async () => {
    await deleteRequest(
      { id: measurementRequest.id.toString() },
      {
        onError: (err) => toast.error(`Error: ${err.message}`),
        onSuccess: () => {
          toast.success(
            `Successfully deleted the Measurement Request "${measurementRequest.name}"!`,
          );
          mutate(`/measurement_requests/${measurementRequestId}`, undefined);
          navigate("/measurement-requests");
        },
      },
    );
  };

  const goBack = () => {
    navigate(-1);
  };

  const copyMeasurementRequestToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(measurementRequest.id.toString());
      // optionally show success UI
    } catch (err) {
      // fallback handled elsewhere if desired
      console.error("Copy failed", err);
    }
  };

  const canDeleteRequest =
    hasPermission("measurement_requests", "delete") &&
    isMeasurementRequestDeletable(measurementRequest.waferRequests.map((wafer) => wafer.status));
  const canUpdateRequest = hasPermission("measurement_requests", "update");

  return (
    <div className="relative mx-auto container p-8 space-y-6">
      <Tooltip
        asChild
        content="Go back"
        trigger={
          <Button variant="outline" size="icon" onClick={goBack}>
            <ArrowLeft />
          </Button>
        }
        side="right"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              {measurementRequest.name}{" "}
              <Tooltip
                asChild
                trigger={
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={copyMeasurementRequestToClipboard}
                    type="button"
                  >
                    <ClipboardCopy />
                  </Button>
                }
                content="Copy Measurement Request ID"
              />
            </h1>
            <div className="flex items-center gap-6 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                Mask: <MaskBadge maskName={measurementRequest.mask.name} />
              </span>
              <span>
                Wafers: <span className="font-medium text-foreground">---</span>
              </span>
              <span>
                Dies: <span className="font-medium text-foreground">---</span>
              </span>
              <span>
                Remaining: <span className="font-medium text-foreground">---</span>
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {measurementRequest.testLabel.isArchived || !canUpdateRequest ? null : (
            <Button type="button" onClick={() => setShowUpdateMeasurementRequestDialog(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          {canDeleteRequest ? (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setIsDeleteActionDialogOpen(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          ) : null}
        </div>
      </div>

      {measurementRequest.testLabel.isArchived ? (
        <WarningAlert className={"flex justify-center h-9 items-center self-end"}>
          Measurement Request cannot be modified because the Test Label is archived.
        </WarningAlert>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Project</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-muted-foreground">Project name</div>
                <div className="font-medium">{measurementRequest.project.name}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Target date</div>
                <div className="font-medium">
                  {measurementRequest.targetDate
                    ? displayDate(new Date(measurementRequest.targetDate))
                    : "---"}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Requested By</div>
                <div className="font-medium">{measurementRequest.createdBy || "---"}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Lot info</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-muted-foreground">Physical location</div>
                <div className="font-medium">{measurementRequest.physicalLocation || "---"}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Measurement Key</div>
                <div className="font-medium">{measurementRequest.measurementKey || "---"}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">IMEC Lot ID</div>
                <div className="font-medium">{measurementRequest.imecLotId || "---"}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">SFAB Lot ID</div>
                <div className="font-medium">{measurementRequest.sfabLotId || "---"}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {measurementRequest.imecLotId ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {pholis.enabled && (
            <PholisScanCard
              measurementRequestId={measurementRequest.id}
              lotId={measurementRequest.imecLotId}
            />
          )}
          <div className={`${pholis.enabled ? "lg:col-span-2" : "lg:col-span-3"} flex`}>
            <WaferSection measurementRequest={measurementRequest} />
          </div>
        </div>
      ) : (
        <WaferSection measurementRequest={measurementRequest} />
      )}

      <MeasurementDiagnostics measurementRequest={measurementRequest} measurements={measurements} />

      <MeasurementRequestMessages
        measurements={measurements}
        measurementRequest={measurementRequest}
        refetch={refetchMeasurementRequestComments}
      />

      <DeleteActionDialog
        action={onDelete}
        description={`Are you sure you want to delete Measurement Request: "${measurementRequest.name}"?`}
        isExecutingAction={isExecutingDeletion}
        open={isDeleteActionDialogOpen}
        onOpenChange={setIsDeleteActionDialogOpen}
        title="Delete Measurement Request"
      />

      {showUpdateMeasurementRequestDialog ? (
        <UpdateMeasurementRequestDialogWrapper
          chipPos={chipPos}
          measurementRequestId={measurementRequest.id}
          onOpenChange={() => setShowUpdateMeasurementRequestDialog(false)}
          open
          testLabels={testLabels}
        />
      ) : null}
    </div>
  );
}
